/**
 * The account dropdown: username and trading wallet, trading-key state,
 * Settings, Sign out.
 *
 * Small, but it carries the one piece of copy that answers the question users
 * actually have — *why is my trading key off* — and the one action docs/04
 * §3.2.4 warns is easy to get wrong: **sign out does not revoke the key.**
 *
 * **The three actions are one list and look like one list.** They used to be
 * two `Link`s in `text-secondary` and a ghost `<Button>`, which renders at a
 * different weight and a different height — so the only bold line in the panel
 * was `Sign out`, and it read as a heading for the paragraph underneath it
 * rather than as the control it is. Nothing said which lines were pressable.
 * Every item now shares `ITEM`: one height, one colour, a full-row hover, and a
 * `▸` on the two that navigate. The identity block above them is the only thing
 * that is *not* an item, and it is styled so that it cannot be mistaken for one.
 */

import { useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { Button, Glyph } from '@/components/primitives'
import { useStore } from '@/app/use-store'
import { useServerNow } from '@/app/use-server-now'
import { useCopy } from '@/app/use-copy'
import { cx } from '@/lib/cx'
import { displayNameOf, type IdentityStore } from '@/wallet/identity'
import { useWallet } from './WalletProvider'
import { truncateAddress } from './truncate-address'

export interface AccountMenuProps {
  store: IdentityStore
  onClose: () => void
}

/**
 * One row, whatever element it is built from.
 *
 * `min-h` below 768px only, the same concession `<Header>` makes: components.md
 * §1's 44px hit-target floor, reverted at `md` so the desktop menu keeps its
 * designed density.
 */
const ITEM = cx(
  'flex min-h-[var(--ud-tap-min)] w-full items-center justify-between gap-2 md:min-h-[unset]',
  'rounded-sm px-1 py-1_5 text-left text-body whitespace-nowrap',
  'text-text-primary hover:bg-bg-hover',
  'focus-visible:shadow-focus focus-visible:outline-none',
)

/** `in 6h`, `in 42m` — coarse on purpose; a key's expiry is not a countdown. */
export function formatKeyExpiry(remainingMs: number): string {
  if (remainingMs <= 0) return 'expired'
  const minutes = Math.floor(remainingMs / 60_000)
  if (minutes < 60) return `in ${minutes}m`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `in ${hours}h`
  return `in ${Math.floor(hours / 24)}d`
}

/** Copy, drawn — `<Glyph>` holds only the six mono-face symbols (§F17), as
 *  the header's account icon is drawn for the same reason. */
function CopyIcon() {
  return (
    <svg viewBox="0 0 16 16" className="h-4 w-4" fill="none" aria-hidden="true">
      <rect x="5.5" y="5.5" width="8" height="8" rx="1.5" stroke="currentColor" strokeWidth="1.2" />
      <path
        d="M10.5 3.5v-.5A1.5 1.5 0 0 0 9 1.5H4A1.5 1.5 0 0 0 2.5 3v5A1.5 1.5 0 0 0 4 9.5h.5"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
      />
    </svg>
  )
}

/** The copy icon's confirmation, for the moment after it lands. */
function CheckIcon() {
  return (
    <svg viewBox="0 0 16 16" className="h-4 w-4" fill="none" aria-hidden="true">
      <path d="M3 8.5l3 3 7-7" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

export function AccountMenu({ store, onClose }: AccountMenuProps) {
  const state = useStore(store)
  const wallet = useWallet()
  // 30s, not the 4 Hz default. The label is `in 6h` — re-rendering it 240 times
  // a minute would buy nothing and put an open dropdown on the same cadence as
  // the countdown.
  const nowMs = useServerNow(30_000)
  const panel = useRef<HTMLDivElement>(null)
  const name = displayNameOf(state)
  const { copied, copy } = useCopy()

  useEffect(() => {
    const onPointerDown = (event: PointerEvent) => {
      if (!panel.current?.contains(event.target as Node)) onClose()
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [onClose])

  return (
    <div
      ref={panel}
      role="menu"
      aria-label="Account"
      // **Sized off the longest item, not off the address.** `Sessions &
      // devices` is ~114px at `body`, so this is that plus a caret and the
      // padding — the narrowest panel that still keeps every item on one line,
      // which is the thing that made `w-64` look like prose.
      //
      // The address does not get a say: it is the short form since §F136,
      // with the full one a tooltip and a copy away. Fitting 42 characters of
      // mono on one line costs ~280px, and wrapping them read as noise.
      className="absolute top-full right-0 z-30 mt-1 w-56 rounded-md border border-border-default bg-bg-surface p-2 shadow-lg"
    >
      {/* **The identity block, and it is deliberately not an item.** It has no
          hover and no row: it is a value to read, and its one control is the
          copy icon beside the address. */}
      <div className="border-b border-border-subtle px-1 pb-2">
        {/* **The username, when there is one, and the wallet under it** —
            `docs/06` §F136. The header chip shows the name instead of the
            address, so this is where the address is one click away: labelled
            as the wallet this account trades from, with a copy icon, because
            a name is what people read and the address is what they paste. */}
        {name !== null ? (
          <div className="mb-1_5 truncate text-body font-semibold text-text-primary" title={name}>
            {name}
          </div>
        ) : null}
        <div className="text-caption uppercase text-text-tertiary">Trading wallet</div>
        {/* **The short form, with a copy icon beside it** — the full address
            wrapped over three lines of mono read as noise (§F136). The full
            one is the row's tooltip and is what Copy puts on the clipboard. */}
        <div className="mt-0_5 flex items-center gap-1" title={state.address ?? undefined}>
          <span className="font-mono text-caption text-text-primary">{truncateAddress(state.address ?? '')}</span>
          <button
            type="button"
            aria-label={copied ? 'Copied' : 'Copy wallet address'}
            title={copied ? 'Copied' : 'Copy wallet address'}
            onClick={() => state.address && copy(state.address)}
            // The 44px floor below 768px, like every item under it.
            className={cx(
              'inline-flex min-h-[var(--ud-tap-min)] min-w-[var(--ud-tap-min)] items-center justify-center rounded-sm md:min-h-0 md:min-w-0 md:p-0_5',
              'hover:bg-bg-hover hover:text-text-primary focus-visible:shadow-focus focus-visible:outline-none',
              copied ? 'text-success-text' : 'text-text-tertiary',
            )}
          >
            {copied ? <CheckIcon /> : <CopyIcon />}
          </button>
        </div>
        <div className="mt-0_5 text-caption text-text-tertiary">
          {state.key === 'ABSENT' ?
            'Trading not enabled'
          : state.keyExpiresAtMs === null ?
            'Trading enabled'
          : `Trading key expires ${formatKeyExpiry(state.keyExpiresAtMs - nowMs)}`}
        </div>

        {/* docs/07 §6. Offered rather than demanded: `RotationScheduler` renews
            this automatically at the next `T+20`, and a user who would rather
            not be interrupted mid-market can take it now instead. */}
        {state.key === 'ROTATE_DUE' ? (
          <Button
            variant="secondary"
            size="sm"
            className="mt-1_5"
            onClick={() => {
              wallet.promptEnableTrading()
              onClose()
            }}
          >
            Renew
          </Button>
        ) : null}
      </div>

      {/*
        **`/money` is reachable from here and from nowhere else — B8.**

        A user adds funds once before they can trade (`wallet/funding.ts`,
        `backend/GASLESS-PLAN.md`), and withdraws here too. It is not in `NAV`
        deliberately: the header's four items are *the product*, and a fifth
        that says "Balance" beside "Portfolio" would read as a second view of
        the same thing. This menu is where the account's own surfaces live —
        keys, and now the money those keys trade — and it is where a user
        looking for "how do I put money in" would open first.
      */}
      {/* Offered until the account has one, and then never again: a name is
          set once (§F136). It opens a window rather than a page, so like
          Sign out it closes the menu first. */}
      {name === null ? (
        <button
          type="button"
          role="menuitem"
          className={cx(ITEM, 'mt-1')}
          onClick={() => {
            onClose()
            wallet.chooseUsername()
          }}
        >
          Choose a username
          <Glyph name="caret" label={null} className="text-text-tertiary" />
        </button>
      ) : null}

      <Link to="/money" role="menuitem" onClick={onClose} className={cx(ITEM, name === null ? 'mt-0_5' : 'mt-1')}>
        Balance
        <Glyph name="caret" label={null} className="text-text-tertiary" />
      </Link>

      <Link to="/settings" role="menuitem" onClick={onClose} className={cx(ITEM, 'mt-0_5')}>
        Sessions &amp; devices
        <Glyph name="caret" label={null} className="text-text-tertiary" />
      </Link>

      {/* Sign out leaves the panel rather than moving within it, so it is ruled
          off from the two that navigate. No `▸`: nothing opens. */}
      <div className="my-1_5 border-t border-border-subtle" />

      <button
        type="button"
        role="menuitem"
        className={ITEM}
        onClick={() => {
          wallet.logout()
          onClose()
        }}
      >
        Sign out
      </button>

      {/* Stated because it is genuinely surprising, and because the alternative
          is a support ticket. docs/04 §3.2.4: logging out on a laptop must not
          kill a key the user is relying on elsewhere. */}
      <p className="mt-1 px-1 text-caption text-text-tertiary">
        Signing out leaves your trading key active on this device. Revoke it in
        Sessions &amp; devices.
      </p>
    </div>
  )
}
